import { useState } from "react";
import { Card, Table, Spin, Alert, Empty, Button, Typography, Tag } from "antd";
import { CalendarOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import NarinLayout from "components/NarinLayout";
import NarinBreadcrumb from "components/Desktop/Breadcrumb";
import useUserStore from "stores/user";
import useLoginModal from "hooks/useLoginModal";
import {
  useGetPointsTransactions,
  getTransactionTypeLabel,
} from "services/pointsTransaction";
import {
  PointsTransactionFilters,
  CapsulePointTransaction,
  PointsTransactionType,
} from "types/pointsTransaction";

const { Title } = Typography;

const PointsHistoryPage = () => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { loginModal, showLogin } = useLoginModal();

  const [filters, setFilters] = useState<PointsTransactionFilters>({
    page: 1,
    limit: 20,
  });

  const {
    transactions,
    currentPage,
    itemsPerPage,
    totalItems,
    isLoading: transactionsLoading,
    error: transactionsError,
    refetch: refetchTransactions,
  } = useGetPointsTransactions(filters, !!user);

  const isLoading = transactionsLoading;
  const hasError = transactionsError;

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const getTypeTag = (type: PointsTransactionType | null) => {
    switch (type) {
      case PointsTransactionType.ORDER:
        return <Tag color="green">{getTransactionTypeLabel(type)}</Tag>;
      case PointsTransactionType.ADMIN_ADJUST:
        return <Tag color="orange">{getTransactionTypeLabel(type)}</Tag>;
      case PointsTransactionType.REFERRAL:
        return <Tag color="blue">{getTransactionTypeLabel(type)}</Tag>;
      case PointsTransactionType.REDEMPTION_DISCOUNT:
      case PointsTransactionType.REDEMPTION_HYBRID:
      case PointsTransactionType.REDEMPTION_POINTS_ONLY:
        return <Tag color="red">{getTransactionTypeLabel(type)}</Tag>;
      default:
        return <Tag>{getTransactionTypeLabel(type)}</Tag>;
    }
  };

  const columns = [
    {
      title: "รายละเอียด",
      dataIndex: "type",
      key: "detail",
      width: "50%",
      render: (_: unknown, record: CapsulePointTransaction) => (
        <div>
          {getTypeTag(record.type)}
          {record.orderId !== null && (
            <div className="text-sm mt-1">
              คำสั่งซื้อ: #{record.orderId}
              {record.finalAmount > 0 && (
                <span className="text-gray-500 ml-2">
                  (฿{record.finalAmount.toLocaleString()})
                </span>
              )}
            </div>
          )}
          {record.type === PointsTransactionType.ADMIN_ADJUST &&
            record.reason && (
              <div className="text-xs text-gray-500 mt-1">
                เหตุผล: {record.reason}
              </div>
            )}
          <div className="text-xs text-gray-400 mt-1">
            {dayjs(record.orderDate).format("DD/MM/YYYY HH:mm")}
          </div>
          {record.orderId !== null && (
            <div className="text-xs text-purple-600 mt-1 font-medium cursor-pointer">
              คลิกเพื่อดูรายละเอียดคำสั่งซื้อ
            </div>
          )}
        </div>
      ),
    },
    {
      title: "สรุปคะแนน",
      dataIndex: "earnPoints",
      key: "points",
      width: "50%",
      align: "center" as const,
      render: (_: unknown, record: CapsulePointTransaction) => (
        <div className="text-center space-y-1">
          {record.earnPoints > 0 && (
            <div className="text-sm font-medium text-green-600">
              +{record.earnPoints.toLocaleString()} คะแนน
            </div>
          )}
          {record.usedPoints > 0 && (
            <div className="text-sm font-medium text-red-600">
              -{record.usedPoints.toLocaleString()} คะแนน
            </div>
          )}
          <div className="text-sm font-semibold text-blue-600">
            คงเหลือ: {record.pointsAfter.toLocaleString()}
          </div>
        </div>
      ),
    },
  ];

  if (!user) {
    return (
      <NarinLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 border">
              <div className="mb-6">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CalendarOutlined className="text-primary-500 text-2xl" />
                </div>
                <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-primary-500 to-primary-700 bg-clip-text text-transparent">
                  ประวัติการเปลี่ยนแปลงคะแนน
                </h1>
                <p className="text-gray-600 mb-6">
                  กรุณาเข้าสู่ระบบเพื่อดูประวัติการใช้คะแนนสะสมของคุณ
                </p>
              </div>
              <button
                onClick={showLogin}
                className="w-full bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors duration-200 font-medium"
              >
                เข้าสู่ระบบ / ลงทะเบียน
              </button>
            </div>
          </div>
          {loginModal}
        </div>
      </NarinLayout>
    );
  }

  if (isLoading) {
    return (
      <NarinLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <Spin size="large" />
            <p className="mt-4">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </NarinLayout>
    );
  }

  if (hasError) {
    return (
      <NarinLayout>
        <div className="container mx-auto px-4 py-8">
          <Alert
            message="เกิดข้อผิดพลาดในการโหลดข้อมูล"
            description="ไม่สามารถโหลดประวัติการใช้คะแนนได้ กรุณาลองใหม่อีกครั้ง"
            type="error"
            showIcon
            action={
              <Button
                onClick={() => {
                  refetchTransactions();
                }}
              >
                ลองใหม่
              </Button>
            }
          />
        </div>
      </NarinLayout>
    );
  }

  return (
    <NarinLayout>
      <NarinBreadcrumb
        items={[
          {
            title: "หน้าแรก",
            path: "/",
          },
          {
            title: "แลกคะแนน",
            path: "/capsule-market",
          },
          {
            title: "ประวัติการเปลี่ยนแปลงคะแนน",
          },
        ]}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-50 ">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Title level={2} className="!mb-2 !text-gray-800">
                <CalendarOutlined className="mr-3" />
                ประวัติการเปลี่ยนแปลงคะแนน
              </Title>
            </div>
            <Button
              type="default"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/capsule-market")}
              className="shadow-sm hover:shadow-md transition-all duration-200"
            >
              กลับไปยังแลกคะแนน
            </Button>
          </div>

          {/* Transactions Table */}
          <Card className="shadow-sm">
            <Table
              columns={columns}
              dataSource={transactions}
              rowKey="id"
              loading={transactionsLoading}
              onRow={(record: CapsulePointTransaction) => ({
                onClick: () => {
                  if (record.orderId !== null) {
                    navigate(`/orderDetail/${record.orderId}`);
                  }
                },
                style: {
                  cursor: record.orderId !== null ? "pointer" : "default",
                },
                className:
                  record.orderId !== null
                    ? "hover:bg-purple-50 transition-colors duration-200"
                    : "",
              })}
              pagination={{
                current: currentPage,
                total: totalItems,
                pageSize: itemsPerPage,
                onChange: handlePageChange,
                showSizeChanger: false,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} จาก ${total} รายการ`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description="ไม่พบรายการ"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        </div>
      </div>
    </NarinLayout>
  );
};

export default PointsHistoryPage;
